/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactElement } from "react";

import MuiRadio, { RadioProps as MuiRadioProps } from "@mui/material/Radio";

import { colors } from "@dotnet/telematics-design-system";

import useRadioIcons from "../../shared/hooks/useRadioIcons";

export interface RadioProps {
  label?: string | ReactElement;
  info?: string;
  isError?: boolean;
}

const Radio = ({
  label,
  info,
  isError,
  ...props
}: RadioProps & MuiRadioProps) => {
  const {
    blue700,
    blue800,
    gray300,
    gray400,
    gray700,
    textDark,
    textDisabled
  } = colors;

  const { getIcons } = useRadioIcons();

  const icon = getIcons(props, isError).icon;
  const checkedIcon = getIcons(props, isError).checkedIcon;

  const isDefault = !props.disabled && !isError;

  return (
    <label
      css={css({
        display: "inline-flex",
        alignItems: "flex-start"
      })}
    >
      <MuiRadio
        {...props}
        size="small"
        icon={icon}
        checkedIcon={checkedIcon}
        sx={{
          padding: "0",
          marginRight: label || info ? "8px" : "0",
          color: gray300,

          "& > svg": {
            fontSize: "24px"
          },

          "&:hover": {
            color: isDefault ? gray400 : "unset"
          },
          "&.Mui-checked": {
            color: blue700
          },
          "&:hover.Mui-checked": {
            color: isDefault ? blue800 : "unset"
          }
        }}
      />

      <div
        css={css({
          cursor: props.disabled ? "auto" : "pointer",
          alignSelf: "center",

          "& > span": {
            display: "block"
          }
        })}
      >
        {label && (
          <span
            css={css({
              color: props.disabled ? textDisabled : textDark,
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "20px",
              letterSpacing: "0.1px"
            })}
          >
            {label}
          </span>
        )}

        {info && (
          <span
            css={css({
              color: props.disabled ? textDisabled : gray700,
              fontSize: "12px",
              fontWeight: "400",
              lineHeight: "16px",
              letterSpacing: "0.4px",
              marginTop: label ? "0" : "2px"
            })}
          >
            {info}
          </span>
        )}
      </div>
    </label>
  );
};

export default Radio;
